import { createContext, useContext, useReducer, useEffect, type ReactNode } from 'react'
import type { TribeId, DemographicKey, AggregatedData, FilterState } from '@/types'

// ============================================
// State Types
// ============================================

interface AppState {
  data: AggregatedData | null
  loading: boolean
  error: string | null
  filters: FilterState
  currentDashboard: string | null
}

type AppAction =
  | { type: 'SET_DATA'; payload: AggregatedData }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_TRIBE'; payload: TribeId }
  | { type: 'SET_BREAK'; payload: { breakType: DemographicKey; breakValue?: string } }
  | { type: 'SET_PROVINCE'; payload: string | undefined }
  | { type: 'SET_SCATTERPLOT_VARS'; payload: { x?: string; y?: string } }
  | { type: 'SET_CURRENT_DASHBOARD'; payload: string | null }
  | { type: 'RESET_FILTERS' }

// ============================================
// Initial State
// ============================================

const FILTER_STORAGE_KEY = 'cpaws-dashboard-filters'

function loadPersistedFilters(): FilterState {
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch {
    // Ignore parse errors
  }
  return {
    selectedTribe: 'total',
    selectedBreak: 'total',
    breakValue: undefined,
    scatterplotX: undefined,
    scatterplotY: undefined,
  }
}

const initialState: AppState = {
  data: null,
  loading: true,
  error: null,
  filters: loadPersistedFilters(),
  currentDashboard: null,
}

// ============================================
// Reducer
// ============================================

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_DATA':
      return { ...state, data: action.payload, loading: false, error: null }

    case 'SET_LOADING':
      return { ...state, loading: action.payload }

    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false }

    case 'SET_TRIBE':
      return {
        ...state,
        filters: { ...state.filters, selectedTribe: action.payload },
      }

    case 'SET_BREAK':
      return {
        ...state,
        filters: {
          ...state.filters,
          selectedBreak: action.payload.breakType,
          breakValue: action.payload.breakValue,
        },
      }

    case 'SET_PROVINCE':
      return {
        ...state,
        filters: {
          ...state.filters,
          selectedProvince: action.payload,
        },
      }

    case 'SET_SCATTERPLOT_VARS':
      return {
        ...state,
        filters: {
          ...state.filters,
          scatterplotX: action.payload.x ?? state.filters.scatterplotX,
          scatterplotY: action.payload.y ?? state.filters.scatterplotY,
        },
      }

    case 'SET_CURRENT_DASHBOARD':
      return { ...state, currentDashboard: action.payload }

    case 'RESET_FILTERS':
      return {
        ...state,
        filters: {
          selectedTribe: 'total',
          selectedBreak: 'total',
          breakValue: undefined,
          scatterplotX: state.filters.scatterplotX,
          scatterplotY: state.filters.scatterplotY,
        },
      }

    default:
      return state
  }
}

// ============================================
// Context
// ============================================

interface AppContextType {
  state: AppState
  dispatch: React.Dispatch<AppAction>
  // Convenience actions
  setTribe: (tribe: TribeId) => void
  setBreak: (breakType: DemographicKey, breakValue?: string) => void
  setProvince: (province: string | undefined) => void
  resetFilters: () => void
}

const AppContext = createContext<AppContextType | undefined>(undefined)

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState)

  // Persist filters to localStorage
  useEffect(() => {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(state.filters))
  }, [state.filters])

  // Convenience actions
  const setTribe = (tribe: TribeId) => {
    dispatch({ type: 'SET_TRIBE', payload: tribe })
  }

  const setBreak = (breakType: DemographicKey, breakValue?: string) => {
    dispatch({ type: 'SET_BREAK', payload: { breakType, breakValue } })
  }

  const setProvince = (province: string | undefined) => {
    dispatch({ type: 'SET_PROVINCE', payload: province })
  }

  const resetFilters = () => {
    dispatch({ type: 'RESET_FILTERS' })
  }

  return (
    <AppContext.Provider value={{ state, dispatch, setTribe, setBreak, setProvince, resetFilters }}>
      {children}
    </AppContext.Provider>
  )
}

export function useApp() {
  const context = useContext(AppContext)
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider')
  }
  return context
}

// Export action types for external use
export type { AppAction, AppState }
